import React, { useState, useEffect, useCallback } from "react";
import { CustomIcon } from "../shared/CustomIcon";
import {
  apiListAnnouncements,
  apiCreateAnnouncement,
  apiUpdateAnnouncement,
  apiAckAnnouncement,
  apiDeleteAnnouncement,
  apiAnnouncementReadReceipts,
} from "./api";

// =============================================================
// AnnouncementsTab — Department Announcements with:
//   • Title + Message
//   • Optional "require acknowledgement" toggle (adds Got it button)
//   • Edit + Delete (poster, dept leader, SA/LA)
//   • Read-receipt expander (leader/admin only)
// One-way: server pushes a notification to every dept member on post.
// The 24h reminder for unack'd ack-required posts is sent by the backend
// (scan on startup + on each GET /announcements list call).
// =============================================================
export default function AnnouncementsTab({ deptId, deptName, currentUser, canPost, deptMemberCount, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [draftAck, setDraftAck] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [openReceipts, setOpenReceipts] = useState(null); // annId
  const [receiptData, setReceiptData] = useState(null);

  const isAdmin = currentUser?.role === "superadmin" || currentUser?.role === "limited_admin";

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiListAnnouncements(deptId);
      const sorted = (Array.isArray(data) ? data : []).slice().sort((a, b) => (b.postedAt || "").localeCompare(a.postedAt || ""));
      setItems(sorted);
    } catch (e) {
      setError(e.message || "Could not load announcements");
    }
    setLoading(false);
  }, [deptId]);
  useEffect(() => { reload(); }, [reload]);

  const resetForm = () => { setDraftTitle(""); setDraftBody(""); setDraftAck(true); setEditingId(null); setShowForm(false); };

  const submit = async () => {
    const title = draftTitle.trim();
    const message = draftBody.trim();
    if (!title) { setError("Title is required."); return; }
    if (!message) { setError("Message body is required."); return; }
    setPosting(true); setError("");
    try {
      if (editingId) {
        await apiUpdateAnnouncement(editingId, { title, message, requireAck: draftAck });
      } else {
        await apiCreateAnnouncement(deptId, { title, message, requireAck: draftAck });
      }
      resetForm();
      await reload();
      onChanged && onChanged();
    } catch (e) {
      setError(e.message || "Could not post announcement");
    }
    setPosting(false);
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setDraftTitle(a.title || "");
    setDraftBody(a.message || "");
    setDraftAck(a.requireAck !== false);
    setShowForm(true);
  };

  const ack = async (annId) => {
    try { await apiAckAnnouncement(annId); reload(); }
    catch (e) { setError(e.message || "Could not acknowledge"); }
  };
  const remove = async (annId) => {
    if (!window.confirm("Delete this announcement? This cannot be undone.")) return;
    try { await apiDeleteAnnouncement(annId); await reload(); onChanged && onChanged(); }
    catch (e) { setError(e.message || "Could not delete"); }
  };
  const openReceiptsFor = async (annId) => {
    if (openReceipts === annId) { setOpenReceipts(null); setReceiptData(null); return; }
    setOpenReceipts(annId); setReceiptData(null);
    try {
      const data = await apiAnnouncementReadReceipts(annId);
      setReceiptData(data);
    } catch (e) {
      setReceiptData({ error: e.message || "Could not load receipts" });
    }
  };

  return (
    <div data-testid="dept-announcements-list">
      {canPost && !showForm && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <button
            className="btn btn-gold"
            data-testid="dept-announcement-new-btn"
            onClick={() => { resetForm(); setShowForm(true); }}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          ><CustomIcon name="megaphone" size={14} /> Post Announcement</button>
        </div>
      )}

      {canPost && showForm && (
        <div className="card" data-testid="dept-announcement-form" style={{ padding: 14, marginBottom: 12 }}>
          <div className="form-group mb-10">
            <label>Title *</label>
            <input
              type="text"
              data-testid="dept-announcement-title-input"
              value={draftTitle}
              onChange={e => setDraftTitle(e.target.value)}
              placeholder="e.g. Sound check this Sunday"
              maxLength={120}
            />
          </div>
          <div className="form-group mb-10">
            <label>Message *</label>
            <textarea
              data-testid="dept-announcement-input"
              value={draftBody}
              onChange={e => setDraftBody(e.target.value)}
              rows={3}
              placeholder="Share the details with your team…"
              style={{ resize: "vertical" }}
            />
          </div>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 10 }}>
            <input
              type="checkbox"
              data-testid="dept-announcement-ack-toggle"
              checked={draftAck}
              onChange={e => setDraftAck(e.target.checked)}
            />
            <span style={{ fontSize: 12, color: "var(--kc-text)", fontFamily: "'Montserrat',sans-serif" }}>
              Require members to tap <strong>“Got it”</strong> to acknowledge
            </span>
          </label>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button className="btn btn-outline btn-sm" onClick={resetForm} disabled={posting}>Cancel</button>
            <button
              className="btn btn-gold btn-sm"
              data-testid="dept-announcement-post-btn"
              disabled={!draftTitle.trim() || !draftBody.trim() || posting}
              onClick={submit}
            >{posting ? (editingId ? "Saving…" : "Posting…") : (editingId ? "Save Changes" : "Post Announcement")}</button>
          </div>
        </div>
      )}

      {error && (
        <div
          data-testid="dept-announcement-error"
          role="alert"
          style={{
            background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 45%, transparent)",
            color: "var(--kc-accent)", borderRadius: 8, padding: "8px 12px", marginBottom: 10,
            fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 600,
          }}
        >{error}</div>
      )}

      {loading ? (
        <div className="widget-empty">Loading announcements…</div>
      ) : items.length === 0 ? (
        <div className="widget-empty-add">
          <div className="wea-icon"><CustomIcon name="megaphone" size={40} /></div>
          <div className="wea-title">No announcements yet</div>
          <div className="wea-sub">
            {canPost
              ? "Be the first to post an update — your team will see it instantly."
              : "Your Department Leader hasn't posted any announcements yet."}
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map(a => {
            const readBy = a.readBy || [];
            const ackedByMe = !!(currentUser?.id && readBy.includes(currentUser.id));
            const total = Math.max(deptMemberCount || 0, readBy.length);
            const canDelete = a.postedBy === currentUser?.id || isAdmin || canPost; // canPost ≈ leader
            const canEdit = canDelete;
            const requireAck = a.requireAck !== false;
            const canSeeReceipts = canPost || isAdmin;
            return (
              <div
                key={a.id}
                data-testid={`dept-announcement-${a.id}`}
                className="card"
                style={{ padding: 14 }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    {a.title && (
                      <div data-testid={`dept-announcement-title-${a.id}`} style={{ fontFamily: "'Cinzel',serif", fontSize: 16, color: "var(--kc-text)", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 4 }}>
                        {a.title}
                      </div>
                    )}
                    <div style={{ fontFamily: "'Cinzel',serif", fontSize: 11, color: "var(--kc-accent)", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                      {a.postedByName || "—"}
                    </div>
                    <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "var(--kc-meta)" }}>
                      {a.postedAt ? new Date(a.postedAt).toLocaleString() : ""}
                      {a.editedAt && <span style={{ marginLeft: 6, fontStyle: "italic", color: "var(--kc-meta)" }}>(edited)</span>}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {canSeeReceipts && (
                      <button
                        className="btn btn-sm btn-outline"
                        data-testid={`dept-announcement-readcount-${a.id}`}
                        title={`${readBy.length} of ${total} acknowledged`}
                        onClick={() => openReceiptsFor(a.id)}
                      >
                        {readBy.length} / {total} read {openReceipts === a.id ? "▴" : "▾"}
                      </button>
                    )}
                    {canEdit && (
                      <button
                        className="btn btn-sm btn-outline"
                        data-testid={`dept-announcement-edit-${a.id}`}
                        onClick={() => startEdit(a)}
                        title="Edit announcement"
                      ><CustomIcon name="edit" size={12} /></button>
                    )}
                    {canDelete && (
                      <button
                        className="btn btn-sm btn-danger"
                        data-testid={`dept-announcement-delete-${a.id}`}
                        onClick={() => remove(a.id)}
                        title="Delete announcement"
                      ><CustomIcon name="close" size={12} /></button>
                    )}
                  </div>
                </div>

                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, color: "var(--kc-text)", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                  {a.message}
                </div>

                {requireAck && (
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                    {ackedByMe ? (
                      <span
                        data-testid={`dept-announcement-acked-${a.id}`}
                        style={{
                          fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                          color: "rgba(74,222,128,0.95)", padding: "5px 10px",
                          background: "rgba(74,222,128,0.10)", border: "1px solid rgba(74,222,128,0.40)",
                          borderRadius: 999, fontFamily: "'Montserrat',sans-serif",
                        }}
                      ><CustomIcon name="check" size={12} /> Got it</span>
                    ) : (
                      <button
                        className="btn btn-sm btn-gold"
                        data-testid={`dept-announcement-ack-${a.id}`}
                        onClick={() => ack(a.id)}
                      >Got it</button>
                    )}
                  </div>
                )}

                {openReceipts === a.id && (
                  <div
                    data-testid={`dept-announcement-receipts-${a.id}`}
                    style={{
                      marginTop: 12, paddingTop: 12, borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)",
                    }}
                  >
                    {!receiptData ? (
                      <div style={{ fontSize: 12, color: "var(--kc-sub)" }}>Loading read receipts…</div>
                    ) : receiptData.error ? (
                      <div style={{ fontSize: 12, color: "#ff6b6b" }}>{receiptData.error}</div>
                    ) : (
                      <>
                        <div style={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--kc-accent)", fontWeight: 700, fontFamily: "'Montserrat',sans-serif", marginBottom: 8 }}>
                          Read receipts — {receiptData.read} of {receiptData.total} acknowledged
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 6 }}>
                          {(receiptData.rows || []).map(r => (
                            <div key={r.memberId} style={{
                              display: "flex", alignItems: "center", gap: 6, padding: "6px 10px",
                              borderRadius: 8,
                              background: r.hasRead ? "rgba(74,222,128,0.10)" : "rgba(255,255,255,0.04)",
                              border: `1px solid ${r.hasRead ? "rgba(74,222,128,0.30)" : "rgba(255,255,255,0.10)"}`,
                              fontFamily: "'Montserrat',sans-serif", fontSize: 12,
                            }}>
                              <span style={{ color: r.hasRead ? "#4ade80" : "rgba(255,255,255,0.40)", display: "inline-flex", alignItems: "center" }}>
                                {r.hasRead ? <CustomIcon name="check" size={12} /> : "○"}
                              </span>
                              <span style={{ color: r.hasRead ? "#FFFFFF" : "rgba(255,255,255,0.70)" }}>{r.name}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
